import { Injectable } from '@angular/core';
import { VueHttpClientFactoryService, VueJavascriptUtilityService } from '@vue/vueframework';
import { Observable } from 'rxjs';
import { Client } from '../types/Client';
import { User } from '../types/User';
import { UserSearchType } from '../types/UserSearchType.enum';
import { PasswordResetOptions } from '../types/PasswordResetOptions';
import { PasswordResetWindow } from '../types/PasswordResetWindow';
import { Params } from '@angular/router';
import { SecurityQuestionAnswer } from '../types/SecurityQuestionAnswer';
import { UpdatePassword } from '../types/UpdatePassword';
import { AppConfigService } from './app-config.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private readonly baseUrl: string;

  public constructor(
    private vueJavascriptUtilityService: VueJavascriptUtilityService,
    private vueHttpClientFactoryService: VueHttpClientFactoryService,
    private appConfig: AppConfigService
  ) {
    this.baseUrl = this.vueJavascriptUtilityService.getRestServiceURL(
      '/api/v1', this.appConfig.getAPIUrl()
    );
  }

  /**
   * Validates the passed in client code for allowing the use in the account recovery app
   * @param clientCode string for client code.
   * @param locale string for locale i.e. en_US
   * @return ClientLookupResponse containing the client information as well as the CCID field label format.
   */
  public lookupClient(clientCode: string, locale: string): Observable<Client> {
    const url = `${this.baseUrl}/client/lookup/${clientCode}?locale=${locale}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().get(url);
  }

  /**
   * Validates the passed in client code and user object
   * @param user User object to search the user
   * @param clientCode string for client code.
   * @param userSearchType User Search type search the user by different type of data
   * @return ClientLookupResponse containing the client information as well as the CCID field label format.
   */
  public lookupUsername(user: User, clientCode: string, userSearchType: UserSearchType): Observable<User> {
    const url = `${this.baseUrl}/username/lookup/${userSearchType}/${clientCode}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, user);
  }

  /**
   * Validates the passed in client code and user object
   * @param user Object with username field to search for reset options.
   * @param clientCode string for client code.
   * @param localeTag the locale for security questions
   * @return PasswordResetOptions containing the hidden email and sms, and security questions for user.
   */
  public lookupPasswordResetOptions(
    user: Params,
    clientCode: string,
    localeTag: string
  ): Observable<PasswordResetOptions> {
    const url = `${this.baseUrl}/password/reset/options/${clientCode}?locale=${localeTag}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, user);
  }

  /**
   * Verify user security question answers
   * @param questions An SecurityQuestionAnswer object that has user's username, and questions with answer and ids
   * @param clientCode The string for client code.
   * @return PasswordResetWindow containing the passwordresetwindowid and hash.
   */
  public lookupSecurityQuestions(
    questions: SecurityQuestionAnswer,
    clientCode: string
  ): Observable<PasswordResetWindow> {
    const url = `${this.baseUrl}/password/reset/securityQuestions/${clientCode}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, questions);
  }

  /**
   * Send Password reset link by email
   * @param user An object with `username` as key with username to send reset link as value.
   * @param clientCode The string for the client code.
   * @return response from endpoint expected None for 200 and 400 for validationFailedException
   */
  public sendEmail(user: User, clientCode: string): Observable<any> {
    const url = `${this.baseUrl}/password/reset/email/${clientCode}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, user);
  }

  /**
   * Send verification code
   * @param user An Object with `username` as key with username to send reset link as value.
   * @param clientCode The string for the client code.
   * @return response from endpoint expected None for 200 and 400 for validationFailedException
   */
  public sendVerificationCode(user: User, clientCode: string): Observable<any> {
    const url = `${this.baseUrl}/password/reset/request/verificationcode/${clientCode}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, user);
  }

  /**
   * Makes a call to AccountServices endpoint to validate the PasswordResetWindow
   * @param passwordResetWindow A PasswordResetWindow Object that has the hash and id of the resetWindow
   * @param clientCode The string for the client code.
   * @return response from endpoint expected None for 200 and 400 when validationFailedException
   */
  public validatePasswordWindow(passwordResetWindow: PasswordResetWindow, clientCode: string): Observable<any> {
    const url = `${this.baseUrl}/password/validate/${clientCode}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, passwordResetWindow);
  }

  /**
   * Makes a call to AccountServices endpoint to update user's password
   * @param updatePassword An UpdatePassword object that has the password to update and
   * the hash and id of the resetWindow.
   * @param clientCode The string for the client code.
   * @return response from endpoint expected None for 200 and 400 when validationFailedException
   */
  public updatePassword(updatePassword: UpdatePassword, clientCode: string): Observable<any> {
    const url = `${this.baseUrl}/password/update/${clientCode}`;
    return this.vueHttpClientFactoryService.getVueHttpClient().post(url, updatePassword);
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
